/**
 * Re-read the named params from the live URL into `params`.
 *
 * Some params are written straight to the address bar with `window.history.replaceState` rather than
 * through the router -- `prior_studies` and `skip_studies` are (see `useActivePriorStudies.ts`), and
 * so are a handful of other viewer params. The router never sees those writes, so the `searchParams`
 * it hands a component can be stale for exactly those keys. A component that then builds its next URL
 * from the router's copy silently reverts them.
 *
 * Call this on the copy you are about to mutate, before mutating it, naming the keys that are written
 * out of band. Absent in the live URL means absent, so a key that has since been removed is deleted
 * rather than left behind; anything not named is untouched.
 *
 * This is a workaround for not being able to trust the router's answer, not a design. The keys should
 * move to router-mediated writes so that no caller has to know which ones are special.
 */
export function syncParamsFromLiveUrl(params: URLSearchParams, keys: readonly string[]): void {
  const live = new URLSearchParams(window.location.search);
  for (const key of keys) {
    const value = live.get(key);
    if (value == null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }
}
